// pages/PortsOrderDetail.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Building2,
  User,
  MoreVertical,
  FileText,
  Activity,
  Save,
  X as CloseIcon,
  Loader,
  PhoneCall,
  Calendar,
  CheckCircle,
  Hash,
  Clock,
} from "lucide-react";
import apiService from "../../services/api";
import EditPortsOrderModal from "../../components/EditPortsOrderModal";
import { useTheme } from "../../contexts/ThemeContext";

// Extracted EditableField component - defined OUTSIDE the parent component
const EditableField = ({
  label,
  field,
  value,
  type = "text",
  options = null,
  readOnly = false,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const { isDarkMode } = useTheme();

  // Format value for editing (especially dates)
  const getEditValue = useCallback(
    (val) => {
      if (type === "date" && val) {
        try {
          const date = new Date(val);
          return date.toISOString().split("T")[0];
        } catch {
          return val || "";
        }
      }
      return val || "";
    },
    [type],
  );

  // Format value for display
  const getDisplayValue = useCallback(
    (val) => {
      if (type === "date" && val) {
        try {
          return new Date(val).toLocaleDateString();
        } catch {
          return val;
        }
      }
      return val || "—";
    },
    [type],
  );

  // Start editing
  const startEditing = useCallback(() => {
    setLocalValue(getEditValue(value));
    setIsEditing(true);
  }, [value, getEditValue]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setLocalValue("");
  }, []);

  // Save changes
  const saveChanges = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await onSave(field, localValue);
      setIsEditing(false);
      setLocalValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  }, [field, localValue, onSave, isSaving]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        cancelEditing();
      } else if (e.key === "Enter" && type !== "textarea") {
        e.preventDefault();
        saveChanges();
      } else if (e.key === "Enter" && e.ctrlKey && type === "textarea") {
        e.preventDefault();
        saveChanges();
      }
    },
    [cancelEditing, saveChanges, type],
  );

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Move cursor to end for text inputs
          if (type === "text" || type === "textarea") {
            const len = inputRef.current.value.length;
            inputRef.current.setSelectionRange(len, len);
          }
        }
      });
    }
  }, [isEditing, type]);

  // Handle click outside to cancel
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        cancelEditing();
      }
    };

    // Delay adding listener to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, cancelEditing]);

  if (readOnly) {
    return (
      <div>
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <p className={isDarkMode ? "text-white" : "text-gray-900"}>
          {getDisplayValue(value)}
        </p>
      </div>
    );
  }

  return (
    <div className="group" ref={containerRef}>
      {label && (
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        {isEditing ? (
          <div className="flex-1 flex items-center gap-2">
            {options ? (
              <select
                ref={inputRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 rounded-lg focus:outline-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
              >
                <option value="">Select...</option>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : type === "textarea" ? (
              <textarea
                ref={inputRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 rounded-lg focus:outline-none resize-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
                rows={3}
              />
            ) : type === "date" ? (
              <input
                ref={inputRef}
                type="date"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 rounded-lg focus:outline-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
              />
            ) : (
              <input
                ref={inputRef}
                type={type}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 rounded-lg focus:outline-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
              />
            )}
            <button
              onClick={saveChanges}
              disabled={isSaving}
              className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              type="button"
            >
              {isSaving ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
            </button>
            <button
              onClick={cancelEditing}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              type="button"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        ) : (
          <>
            <p
              className={`flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {getDisplayValue(value)}
            </p>
            <button
              onClick={startEditing}
              className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              type="button"
            >
              <Edit
                size={14}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const PortsOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [portsOrder, setPortsOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  useEffect(() => {
    fetchPortsOrder();
  }, [id]);

  const fetchPortsOrder = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPortsOrder(id);
      setPortsOrder(response.data);
    } catch (error) {
      console.error("Error fetching ports order:", error);
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this ports order?")) {
      try {
        await apiService.deletePortsOrder(id);
        navigate("/projects");
      } catch (error) {
        console.error("Error deleting ports order:", error);
        alert("Failed to delete ports order");
      }
    }
  };

  // Stable save handler using useCallback
  const handleFieldSave = useCallback(
    async (field, value) => {
      await apiService.updatePortsOrder(id, { [field]: value });
      await fetchPortsOrder();
    },
    [id],
  );

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <Loader
          className={`animate-spin ${isDarkMode ? "text-white" : "text-gray-900"}`}
          size={32}
        />
      </div>
    );
  }

  if (!portsOrder) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Ports order not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Go Back</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
              >
                <Edit size={16} />
                <span>Edit</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200"}`}
                >
                  <MoreVertical
                    size={20}
                    className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                  />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 rounded-lg shadow-xl z-50 border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${isDarkMode ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-50 text-gray-700"}`}
                    >
                      <FileText size={16} />
                      Copy Link
                    </button>
                    <div
                      className={
                        isDarkMode
                          ? "border-t border-gray-700"
                          : "border-t border-gray-200"
                      }
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-2 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Ports Order Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-600 flex items-center justify-center text-white flex-shrink-0">
                  <PhoneCall size={20} />
                </div>
                <div className="min-w-0">
                  <h1
                    className={`text-xl sm:text-3xl font-bold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {portsOrder.portsOrderName}
                  </h1>
                  {portsOrder.portOrderReference && (
                    <div
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {portsOrder.portOrderReference}
                    </div>
                  )}
                </div>

                {portsOrder.portCompleted && (
                  <span className="px-3 py-1 rounded-lg border text-sm font-medium bg-green-500/10 text-green-400 border-green-500/20 flex items-center gap-1 flex-shrink-0">
                    <CheckCircle size={14} />
                    Completed
                  </span>
                )}
              </div>

              <div
                className={`flex flex-wrap items-center gap-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {portsOrder.account && (
                  <button
                    onClick={() =>
                      navigate(`/accounts/${portsOrder.account.id}`)
                    }
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <Building2 size={16} />
                    <span>{portsOrder.account.name}</span>
                  </button>
                )}
                {portsOrder.project && (
                  <button
                    onClick={() =>
                      navigate(`/projects/${portsOrder.project.id}`)
                    }
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <FileText size={16} />
                    <span>{portsOrder.project.projectName}</span>
                  </button>
                )}
                {portsOrder.numbersToPort && (
                  <div className="flex items-center gap-2">
                    <Hash size={16} />
                    <span>
                      {
                        portsOrder.numbersToPort.split(/[\n,]/).filter(Boolean)
                          .length
                      }{" "}
                      numbers
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`rounded-lg border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {[
                  { id: "details", label: "Details", icon: FileText },
                  { id: "activity", label: "Activity", icon: Activity },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-6 py-3 font-medium transition-colors ${
                        activeTab === tab.id
                          ? isDarkMode
                            ? "text-blue-400 border-b-2 border-blue-400"
                            : "text-blue-600 border-b-2 border-blue-600"
                          : isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <PhoneCall size={18} />
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Ports Order Name"
                          field="portsOrderName"
                          value={portsOrder.portsOrderName}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Port Order Reference"
                          field="portOrderReference"
                          value={portsOrder.portOrderReference}
                          onSave={handleFieldSave}
                        />
                      </div>
                    </div>

                    {/* Numbers to Port */}
                    <div
                      className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Hash size={18} />
                        Numbers to Port
                      </h3>
                      <EditableField
                        label=""
                        field="numbersToPort"
                        value={portsOrder.numbersToPort}
                        type="textarea"
                        onSave={handleFieldSave}
                      />
                      {portsOrder.numbersToPort && (
                        <div
                          className={`mt-4 p-4 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                        >
                          <div
                            className={`text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Total Numbers:{" "}
                            {
                              portsOrder.numbersToPort
                                .split(/[\n,]/)
                                .filter(Boolean).length
                            }
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {portsOrder.numbersToPort
                              .split(/[\n,]/)
                              .filter(Boolean)
                              .map((num, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded text-sm"
                                >
                                  {num.trim()}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Dates & Timeline */}
                    <div
                      className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Calendar size={18} />
                        Dates & Timeline
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <EditableField
                          label="Requested Date"
                          field="requestedDate"
                          value={portsOrder.requestedDate}
                          type="date"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Accepted Date"
                          field="acceptedDate"
                          value={portsOrder.acceptedDate}
                          type="date"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Port Date"
                          field="portDate"
                          value={portsOrder.portDate}
                          type="date"
                          onSave={handleFieldSave}
                        />
                      </div>

                      {/* Timeline Visualization */}
                      <div className="mt-6 space-y-3">
                        {portsOrder.requestedDate && (
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                            <div className="flex-1">
                              <div
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                Requested
                              </div>
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {new Date(
                                  portsOrder.requestedDate,
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        )}
                        {portsOrder.acceptedDate && (
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-yellow-500" />
                            <div className="flex-1">
                              <div
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                Accepted
                              </div>
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {new Date(
                                  portsOrder.acceptedDate,
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        )}
                        {portsOrder.portDate && (
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-3 h-3 rounded-full ${
                                portsOrder.portCompleted
                                  ? "bg-green-500"
                                  : "bg-purple-500"
                              }`}
                            />
                            <div className="flex-1">
                              <div
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                Port Date
                              </div>
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {new Date(
                                  portsOrder.portDate,
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    {portsOrder.notes && (
                      <div
                        className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                      >
                        <h3
                          className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          Notes
                        </h3>
                        <EditableField
                          label=""
                          field="notes"
                          value={portsOrder.notes}
                          type="textarea"
                          onSave={handleFieldSave}
                        />
                      </div>
                    )}

                    {/* System Information */}
                    <div
                      className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {portsOrder.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(portsOrder.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {portsOrder.lastModifiedBy?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(portsOrder.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Activity Tab */}
                {activeTab === "activity" && (
                  <div
                    className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <Activity size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Activity tracking coming soon</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
            {/* Quick Info Card */}
            <div
              className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Quick Information
              </h3>
              <div className="space-y-3">
                {portsOrder.account && (
                  <button
                    onClick={() =>
                      navigate(`/accounts/${portsOrder.account.id}`)
                    }
                    className={`w-full p-3 rounded-lg transition-colors text-left ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Building2 size={14} />
                      Account
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {portsOrder.account.name}
                    </div>
                  </button>
                )}

                {portsOrder.project && (
                  <button
                    onClick={() =>
                      navigate(`/projects/${portsOrder.project.id}`)
                    }
                    className={`w-full p-3 rounded-lg transition-colors text-left ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <FileText size={14} />
                      Project
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {portsOrder.project.projectName}
                    </div>
                  </button>
                )}

                {portsOrder.numbersToPort && (
                  <div
                    className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Hash size={14} />
                      Numbers to Port
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {
                        portsOrder.numbersToPort.split(/[\n,]/).filter(Boolean)
                          .length
                      }{" "}
                      numbers
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Card */}
            <div
              className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Status
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Port Status
                  </span>
                  {portsOrder.portCompleted ? (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1">
                      <CheckCircle size={12} />
                      Completed
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs flex items-center gap-1">
                      <Clock size={12} />
                      In Progress
                    </span>
                  )}
                </div>

                {portsOrder.requestedDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Requested
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(portsOrder.requestedDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {portsOrder.acceptedDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Accepted
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(portsOrder.acceptedDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {portsOrder.portDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Port Date
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(portsOrder.portDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {portsOrder.portDate && !portsOrder.portCompleted && (
                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <div className="text-xs text-yellow-400">
                      Port scheduled for{" "}
                      {new Date(portsOrder.portDate).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Numbers Preview Card */}
            {portsOrder.numbersToPort && (
              <div
                className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Numbers Preview
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {portsOrder.numbersToPort
                    .split(/[\n,]/)
                    .filter(Boolean)
                    .slice(0, 5)
                    .map((num, idx) => (
                      <div
                        key={idx}
                        className={`p-2 rounded text-sm flex items-center gap-2 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                      >
                        <Phone size={14} className="text-purple-400" />
                        <span
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {num.trim()}
                        </span>
                      </div>
                    ))}
                  {portsOrder.numbersToPort.split(/[\n,]/).filter(Boolean)
                    .length > 5 && (
                    <div
                      className={`text-xs text-center pt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      +
                      {portsOrder.numbersToPort.split(/[\n,]/).filter(Boolean)
                        .length - 5}{" "}
                      more numbers
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditPortsOrderModal
          portsOrder={portsOrder}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchPortsOrder();
          }}
        />
      )}
    </div>
  );
};

export default PortsOrderDetail;
